<template>
    <div
        ref="Reader"
        class="Reader absolute h-full w-full overflow-hidden bg-theme-bg-reader"
        dusk="Reader"
        :data-theme="ui.themeSelected"
        :style="{
            '--ReaderHeaderHeight': ui.headerbar.offsetHeight + 'px',
            '--ReaderToolbarHeight': ui.toolbar.offsetHeight + 'px',
            '--sidebar-offset-left': sidebarContentOffset.left + 'px',
            '--sidebar-offset-right': sidebarContentOffset.right + 'px',
            '--sidebar-panel-width-left': sidebarPanelWidthLeft,
            '--sidebar-panel-width-right': '380px',
        }"
    >
        <SkipLinks :class="chromeStepsAside" />
        <ReaderHeader :class="chromeStepsAside" />
        <transition name="bookmark-fold">
            <BookmarkFold v-if="canUseBookmarks && isCurrentPageBookmarked" :class="chromeStepsAside" />
        </transition>
        <LoadingSpinner
            v-show="!isUnsupportedFormat && (isLoading || ui.pagination.current !== currentPage)"
            :position="loadingSpinnerPosition"
        />

        <ReaderNotebook v-if="isNotAudiobook" />

        <ReaderFlashcardsPanel v-if="canUseFlashcards" />
        <ReaderAddOnsPanel v-if="canUseAddOns" />

        <template v-if="isAudiobook">
            <AudiobookAbout />
        </template>

        <main id="main-content" role="presentation">
            <ReaderUnsupportedFormat v-if="isUnsupportedFormat" />
            <component
                :is="readerViewer"
                v-else-if="isLoadingProcessFinished && polpoSessionInitiated && readerViewer"
                :key="reader.layout"
            />
        </main>
        <SideControlPaginator v-if="showPaginator" :class="chromeStepsAside" />

        <ReaderThumbnails v-if="isPdf" />
        <ReaderTableOfContents v-if="isNotAudiobook" />
        <transition name="fade">
            <ReaderSelectionTool />
        </transition>
        <ReaderIntegrationsTool v-if="isNotAudiobook" />
        <SessionCountdown v-if="concurrencyLimit.enforce" />
        <SessionExpiredModal />
        <ComponentToast />
        <ReaderTourBanner />
        <FeatureTours />
        <ReaderOnboarding v-if="onboarding.hasToBeShown" />
        <ReaderPromoteAppModal v-if="promoteApp.hasToBeShown" />
        <ComponentTts v-if="isNotAudiobook && ui.listening.opened && ui.listening.enabled" :class="chromeStepsAside" />
        <FlashcardFromSelectionModal v-if="canUseFlashcards" />
        <ComponentBuyOrLoginButton v-if="showBuyOrLoginButton" :class="chromeStepsAside" />
        <SocialShare v-if="isShareEnabled" />
        <ShareCollectionModal />
        <AddToLists v-if="addToLists.show" />
        <template v-if="canUseReaderPlugins">
            <ReaderSidebarPlugin v-for="plugin in shownReaderPlugins" :key="plugin.key" :plugin="plugin" />
        </template>
        <ConfirmationModal />
        <PrintControlModal v-if="ui.printModal.opened" />
        <AddOnBatchDialog v-if="ui.addOnBatch.dialogOpened" />

        <AddOnCaptureHint v-if="addOnEditorAwaitsAnchor" />

        <ReaderFooter v-if="isNotAudiobook" :class="chromeStepsAside" />
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { throttle } from 'lodash';
import { afterNextPaint } from '_shared_/js/utils/afterNextPaint';
import { resolveSidebarLeftWidth } from '_shared_/js/constants/sidebarLayout';
import { stepBack } from '_shared_/js/utils/backSteps';
import ReaderHeader from '_shared_/UI/ReaderHeader.vue';
import BookmarkFold from '_shared_/UI/Components/BookmarkFold.vue';
import ReaderFooter from '_shared_/UI/ReaderFooter.vue';
import ReaderThumbnails from '_shared_/UI/Sidebar/ReaderThumbnails.vue';
import ReaderTableOfContents from '_shared_/UI/Sidebar/ReaderTableOfContents.vue';
import ReaderSidebarPlugin from '_shared_/UI/Sidebar/ReaderSidebarPlugin.vue';
import LoadingSpinner from '_shared_/UI/Utilities/LoadingSpinner.vue';
import SessionExpiredModal from '_shared_/UI/Sessions/SessionExpiredModal.vue';
import ConfirmationModal from '_shared_/UI/Components/ConfirmationModal.vue';
import PrintControlModal from '_shared_/UI/Print/PrintControlModal.vue';
import AddOnBatchDialog from '_shared_/UI/AddOns/AddOnBatchDialog.vue';
import SessionCountdown from '_shared_/UI/SessionsConcurrency/SessionCountdown.vue';
import ReaderSelectionTool from '_shared_/UI/ReaderSelectionTool/ReaderSelectionTool.vue';
import ReaderIntegrationsTool from '_shared_/UI/ReaderSelectionTool/ReaderIntegrationsTool.vue';
import ComponentToast from '_shared_/UI/Components/ComponentToast.vue';
import ReaderOnboarding from '_shared_/UI/ReaderOnboarding/ReaderOnboarding.vue';
import ReaderTourBanner from '_shared_/UI/ReaderOnboarding/ReaderTourBanner.vue';
import FeatureTours from '../tours/FeatureTours.vue';
import ComponentTts from '_shared_/UI/Components/ComponentTts.vue';
import ComponentNetworkStatus from '_shared_/UI/Components/ComponentNetworkStatus.vue';
import ComponentBuyOrLoginButton from '_shared_/UI/Components/ComponentBuyOrLoginButton.vue';
import AudiobookAbout from '../../../_shared_/UI/Audiobooks/AudiobookAbout.vue';
import SocialShare from '_shared_/UI/SocialShare/SocialShare.vue';
import ShareCollectionModal from '_shared_/UI/Sharing/ShareCollectionModal.vue';
import FlashcardFromSelectionModal from '_shared_/UI/Flashcards/FlashcardFromSelectionModal.vue';
import SideControlPaginator from '../../../_shared_/UI/Components/SideControlPaginator.vue';
import ReaderPromoteAppModal from '../../../_shared_/UI/ReaderPromoteApp/ReaderPromoteAppModal.vue';
import handleUrl from '../../../_shared_/js/utils/handleUrl';
import isTouchDevice from '../../../_shared_/js/utils/isTouchDevice';
import AddToLists from '../../../_shared_/UI/Lists/AddToLists.vue';
import ReaderNotebook from '../../../_shared_/UI/Notebook/ReaderNotebook.vue';
import ReaderFlashcardsPanel from '../../../_shared_/UI/Flashcards/ReaderFlashcardsPanel.vue';
import ReaderAddOnsPanel from '../../../_shared_/UI/AddOns/ReaderAddOnsPanel.vue';
import AddOnCaptureHint from '../../../_shared_/UI/AddOns/AddOnCaptureHint.vue';
import SkipLinks from '../../../_shared_/UI/Components/SkipLinks.vue';
import ReaderUnsupportedFormat from './partials/ReaderUnsupportedFormat.vue';
import applyThemeToDocument from '../../../_shared_/js/utils/applyThemeToDocument';
import {
    announceThemeToHost,
    fixedPageViewTracker,
    isPagebreakEmissionMode,
    navigationAPI,
    trackingEventsListener,
    uiAPI,
} from '../communication';

export default {
    el: '#Reader',
    name: 'Reader',
    components: {
        ReaderTableOfContents,
        ReaderHeader,
        BookmarkFold,
        ReaderFooter,
        ReaderNotebook,
        ReaderFlashcardsPanel,
        ReaderAddOnsPanel,
        AddOnCaptureHint,
        ReaderThumbnails,
        ReaderSidebarPlugin,
        LoadingSpinner,
        SessionCountdown,
        SessionExpiredModal,
        ConfirmationModal,
        PrintControlModal,
        AddOnBatchDialog,
        ReaderSelectionTool,
        ReaderIntegrationsTool,
        ComponentToast,
        ReaderOnboarding,
        ReaderTourBanner,
        FeatureTours,
        ComponentTts,
        ComponentNetworkStatus,
        ComponentBuyOrLoginButton,
        AudiobookAbout,
        SocialShare,
        ShareCollectionModal,
        FlashcardFromSelectionModal,
        ReaderPromoteAppModal,
        AddToLists,
        SideControlPaginator,
        SkipLinks,
        ReaderUnsupportedFormat,
    },
    computed: {
        ...mapState([
            'concurrencyLimit',
            'isLoadingProcessFinished',
            'polpoSessionInitiated',
            'fileType',
            'publication',
            'reader',
            'isFixedLayout',
            'isLoading',
            'isInitialContentReady',
            'loadingSpinnerPosition',
            'ui',
            'device',
            'uiHidden',
            'coniglio',
            'volpeHost',
            'onboarding',
            'lastLocation',
            'promoteApp',
            'addToLists',
            'issue',
        ]),
        ...mapGetters([
            'isEpub',
            'isPdf',
            'isAudiobook',
            'hasTextContent',
            'isUnsupportedFormat',
            'currentPage',
            'hasFeatureActive',
            'showBuyOrLoginButton',
            'onlyDesktopVisible',
            'scrollPageMode',
            'isZoomActive',
            'isShareEnabled',
            'canUseReaderPlugins',
            'shownReaderPlugins',
            'sidebarContentOffset',
            'isCurrentPageBookmarked',
            'canUseBookmarks',
            'canUseFlashcards',
            'isWebHost',
            'canUseAddOns',
            'addOnEditorAwaitsAnchor',
        ]),
        /**
         * Asking the page for an anchor is the page's moment: the reader's
         * controls step aside until it answers. Hidden rather than
         * unmounted, so the bands the header and the footer reserve stay
         * reserved and the content is not repaginated under the gesture that
         * is choosing where the piece goes.
         * @returns {object} A class binding for a reader control
         */
        chromeStepsAside() {
            return { invisible: this.addOnEditorAwaitsAnchor };
        },
        /**
         * @returns {string} CSS width of the left panel, tracking the reader as it resizes.
         */
        sidebarPanelWidthLeft() {
            return `${resolveSidebarLeftWidth({
                leftWidth: this.ui.sidebar.leftWidth,
                viewportWidth: this.device.width || window.innerWidth,
            })}px`;
        },
        readerViewer() {
            if (this.isPdf) {
                return this.reader.layout === 'scroll'
                    ? () => import('./partials/Pdf/PdfScrollReaderViewer.vue')
                    : () => import('./partials/Pdf/PdfPagedReaderViewer.vue');
            }
            if (this.isEpub) {
                return () => import('./partials/Epub/EpubViewer.vue');
            }
            if (this.isAudiobook) {
                return () => import('./partials/Audiobook/AudiobookViewer.vue');
            }
            return undefined;
        },
        isNotAudiobook() {
            return this.isPdf || this.isEpub;
        },
        showPaginator() {
            // Determines whether to display the paginator.
            // Hides the paginator if in scroll mode, audiobook mode, an unsupported format,
            // or on touch devices in mobile view.
            // On non-touch devices, always show paginator (needed for browser zoom accessibility).

            const isScrollModeOrAudiobook = this.scrollPageMode || this.isAudiobook;
            const isMobileViewOnTouchDevice = !this.onlyDesktopVisible && isTouchDevice;
            const isZoomAndUiVisible = this.isZoomActive && !this.uiHidden;

            if (isScrollModeOrAudiobook || isMobileViewOnTouchDevice || this.isUnsupportedFormat) {
                return false;
            }

            return !this.isZoomActive || isZoomAndUiVisible;
        },
    },
    watch: {
        isFixedLayout(isFixed) {
            if (isFixed) {
                this.initFixedPageViewTracker();
            }
        },
        isLoadingProcessFinished(finished) {
            if (finished) {
                this.onLoadingProcessFinished();
            }
        },
        'ui.themeSelected'(theme) {
            applyThemeToDocument(theme);
            if (this.isLoadingProcessFinished) this.announceAppliedTheme();
        },
        isInitialContentReady: {
            immediate: true,
            handler(isReady) {
                this.cancelReadyAnnouncement?.();
                if (!isReady) return;

                // Before the shared link, whose piece opens over this panel.
                this.openInitialPanel();
                // The first content on screen is the first moment the reader can
                // be turned, which is what a shared add-on link is waiting for.
                this.openSharedAddOn();
                this.cancelReadyAnnouncement = afterNextPaint(() => {
                    if (this.isInitialContentReady) void uiAPI.readinessChanged({ isReady: true });
                });
            },
        },
    },
    mounted() {
        this.detectScreenSize();
        this.throttledDetectScreenSize = throttle(this.detectScreenSize, 100);
        window.addEventListener('resize', this.throttledDetectScreenSize, true);
        this.watchNetworkStatus();
        document.addEventListener('keydown', this.handleKeyPressed, false);
        document.addEventListener('click', this.routeExternalLink, true);
        trackingEventsListener.init(this.$bus, {
            isPagebreakMode: () =>
                isPagebreakEmissionMode({
                    isEpub: this.$store.getters.isEpub,
                    hasPagebreaks: this.$store.state.issue.hasPagebreaks,
                    isFixedLayout: this.$store.state.isFixedLayout,
                }),
            isEpub: () => this.$store.getters.isEpub,
            isLoading: () => this.$store.state.isLoading,
        });

        // Defensive: handle the case where the loading process already
        // finished before this component mounted (e.g. on remounts).
        if (this.isLoadingProcessFinished) {
            this.onLoadingProcessFinished();
        }
    },
    beforeDestroy() {
        this.cancelReadyAnnouncement?.();
        fixedPageViewTracker.destroy();
        window.removeEventListener('resize', this.throttledDetectScreenSize, true);
        this.throttledDetectScreenSize.cancel();
        document.removeEventListener('keydown', this.handleKeyPressed);
        document.removeEventListener('click', this.routeExternalLink, true);
    },
    methods: {
        ...mapMutations([
            'setDeviceDetails',
            'setTotalPages',
            'setIssueId',
            'setNetworkStatus',
            'closeAllSettingsOrFeatures',
        ]),
        ...mapActions([
            'goToPrevPage',
            'goToNextPage',
            'openSearch',
            'notifyLocationChange',
            'toggleUi',
            'openInitialPanel',
            'openSharedAddOn',
            'cancelAddOnEditorAnchorCapture',
        ]),
        routeExternalLink(event) {
            handleUrl(event, url => navigationAPI.openLink({ url }), { nativeLinks: this.isWebHost });
        },
        onLoadingProcessFinished() {
            // A light theme may match the store default without firing its watcher.
            applyThemeToDocument(this.ui.themeSelected);
            this.announceAppliedTheme();
            // The store's default fileType is 'pdf', so isPdf is only reliable
            // once loading finished.
            if (this.isPdf) {
                this.initFixedPageViewTracker();
            }
        },
        initFixedPageViewTracker() {
            fixedPageViewTracker.init(
                this.$bus,
                () => this.$store.getters.currentPages,
                this.$store.state.reader.pageViewConfirmDelayMs
            );
        },
        announceAppliedTheme() {
            this.$nextTick(() => announceThemeToHost(this.ui.themeSelected, this.$refs['Reader']));
        },
        detectScreenSize() {
            this.setDeviceDetails({
                width: this.$refs['Reader'].clientWidth,
                height: this.$refs['Reader'].clientHeight,
                orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
            });
        },
        handleKeyPressed(e) {
            if (!this.hasTextContent) return;

            if (this.reader.keyboardNavigationEnabled) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.goToPrevPage();
                }
                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.goToNextPage();
                }
            }
            // A capture answers Escape before the panels do: the operator is
            // leaving the question the page was asked, not the piece they are
            // writing, which stays open.
            if (this.addOnEditorAwaitsAnchor && e.key === 'Escape') {
                (e.preventDefault(), this.cancelAddOnEditorAnchorCapture());
            } else if (e.key === 'Escape') {
                // Escape answers like back, one step at a time. This listener runs before
                // the open panel's own focus trap, which would take a second step. Panels
                // `hasFeatureActive` leaves out, such as add-ons, step back here too.
                if (stepBack()) (e.preventDefault(), e.stopImmediatePropagation());
                else if (this.hasFeatureActive) (e.preventDefault(), this.closeAllSettingsOrFeatures());
            }
            if (this.ui.search.enabled && (e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
                (e.preventDefault(), this.toggleUi({ uiHidden: false }), this.openSearch());
            }
        },
        watchNetworkStatus() {
            this.setNetworkStatus({
                isOffline: !navigator.onLine,
            });
            window.addEventListener('online', () => {
                window.app = window.app || {};
                window.app.network = 'online';
                this.setNetworkStatus({
                    isOffline: false,
                });
            });
            window.addEventListener('offline', () => {
                window.app = window.app || {};
                window.app.network = 'offline';
                this.setNetworkStatus({
                    isOffline: true,
                });
            });
        },
    },
};
</script>

<style>
#main-content {
    width: 100%;
    height: 100%;
}

.Reader {
    /* Single timing source for the sidebar displacement: the viewers animate
       their content in sync with this padding transition. */
    --sidebar-panel-transition: 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@media screen and (min-width: 571px) {
    .PdfScrollReaderViewer,
    .PdfPagedReaderViewer,
    .AudiobookViewer {
        padding-left: var(--sidebar-offset-left, 0px);
        padding-right: var(--sidebar-offset-right, 0px);
        transition: padding var(--sidebar-panel-transition);
    }
}
</style>
